import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import SupplyService from '../../services/supplyService';
import Button from '../common/Button';
import './Supplies.css';

const Supplies = () => {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [supplies, setSupplies] = useState([]);
  const [filteredSupplies, setFilteredSupplies] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [expiringOnly, setExpiringOnly] = useState(searchParams.get('expiring') === 'true');
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingSupply, setEditingSupply] = useState(null);
  const [deletingSupply, setDeletingSupply] = useState(null);
  const expirationInputRef = useRef(null);
  const openNativeDatePicker = () => {
    try {
      if (expirationInputRef.current && typeof expirationInputRef.current.showPicker === 'function') {
        expirationInputRef.current.showPicker();
      }
    } catch (e) {
      // ignore browsers that restrict programmatic open
    }
  };
  const [formData, setFormData] = useState({
    name: '',
    expirationDate: '',
    counter: ''
  });

  const loadSupplies = useCallback(async () => {
    try {
      setInitialLoading(true);
      const token = auth.user?.access_token;
      const response = await SupplyService.getAllSupplies(token);
      setSupplies(response.data || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar los insumos'
      });
    } finally {
      setInitialLoading(false);
    }
  }, [auth.user?.access_token]);

  // Load supplies on component mount
  useEffect(() => {
    loadSupplies();
  }, [loadSupplies]);

  useEffect(() => {
    setExpiringOnly(searchParams.get('expiring') === 'true');
  }, [searchParams]);

  // Filter supplies when search filter or supplies change
  useEffect(() => {
    let list = supplies;
    if (searchFilter.trim()) {
      list = list.filter(supply =>
        supply.name.toLowerCase().includes(searchFilter.toLowerCase())
      );
    }
    if (expiringOnly) {
      list = list.filter(s => {
        const du = daysUntil(s?.expirationDate);
        return du !== null && du <= 30;
      });
    }
    setFilteredSupplies(list);
  }, [searchFilter, supplies, expiringOnly]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const handleExpiringToggleChange = (e) => {
    const checked = e.target.checked;

    setExpiringOnly(checked);
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);

      if (checked) {
        nextParams.set('expiring', 'true');
      } else {
        nextParams.delete('expiring');
      }

      return nextParams;
    });
  };

  const clearSearch = () => {
    setSearchFilter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      
      const submitData = {
        ...formData
      };
      if (!submitData.expirationDate) {
        delete submitData.expirationDate;
      }

      if (submitData.counter !== '' && submitData.counter !== null && submitData.counter !== undefined) {
        submitData.counter = Number(submitData.counter);
      } else {
        submitData.counter = null; // Explicitly clear/nullify counter if empty
      }
      
      if (editingSupply) {
        // Update existing supply
        await SupplyService.updateSupply(editingSupply.id, submitData, token);
        setMessage({
          type: 'success',
          text: 'Insumo actualizado exitosamente'
        });
      } else {
        // Create new supply
        await SupplyService.createSupply(submitData, token);
        setMessage({
          type: 'success',
          text: 'Insumo creado exitosamente'
        });
      }

      // Reload supplies and close modal
      await loadSupplies();
      handleCloseModal();
    } catch (error) {
      setMessage({
        type: 'error',
        text: editingSupply 
          ? 'Error al actualizar el insumo' 
          : 'Error al crear el insumo'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = useCallback((supply) => {
    setEditingSupply(supply);
    let expValue = '';
    if (supply && supply.expirationDate) {
      const d = new Date(supply.expirationDate);
      if (!Number.isNaN(d.getTime())) {
        expValue = d.toISOString().substring(0, 10);
      }
    }
    setFormData({
      name: supply.name || '',
      expirationDate: expValue,
      counter: supply.counter !== undefined && supply.counter !== null ? supply.counter : ''
    });
    setShowModal(true);
  }, []);

  // Open edit modal if query param is present
  useEffect(() => {
    const editId = searchParams.get('editId');
    if (editId && supplies.length > 0) {
      // eslint-disable-next-line eqeqeq
      const supplyToEdit = supplies.find(s => s.id == editId);
      if (supplyToEdit) {
        handleEdit(supplyToEdit);
        // Clear param to avoid reopening
        setSearchParams(params => {
          const newParams = new URLSearchParams(params);
          newParams.delete('editId');
          return newParams;
        });
      }
    }
  }, [supplies, searchParams, setSearchParams, handleEdit]);

  const handleDelete = (supply) => {
    setDeletingSupply(supply);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSupply(null);
    setFormData({
      name: '',
      expirationDate: '',
      counter: ''
    });
    setMessage({ type: '', text: '' });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingSupply(null);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    if (!deletingSupply) return;

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      await SupplyService.deleteSupply(deletingSupply.id, token);
      
      setMessage({
        type: 'success',
        text: 'Insumo eliminado exitosamente'
      });

      // Reload supplies and close modal
      await loadSupplies();
      handleCloseDeleteModal();
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al eliminar el insumo'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (isoOrDateString) => {
    if (!isoOrDateString) return '-';
    const d = new Date(isoOrDateString);
    if (Number.isNaN(d.getTime())) return '-';
    const day = d.toLocaleString('es-MX', { day: '2-digit', timeZone: 'UTC' });
    const month = d
      .toLocaleString('es-MX', { month: 'short', timeZone: 'UTC' })
      .replace('.', '')
      .toLowerCase();
    const year = d.toLocaleString('es-MX', { year: 'numeric', timeZone: 'UTC' });
    return `${day} ${month} ${year}`;
  };

  const daysUntil = (isoOrDateString) => {
    if (!isoOrDateString) return null;
    const now = new Date();
    const d = new Date(isoOrDateString);
    if (Number.isNaN(d.getTime())) return null;
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.ceil((d.setHours(0,0,0,0) - now.setHours(0,0,0,0)) / msPerDay);
  };

  if (initialLoading) {
    return (
      <div className="supplies-container">
        <div className="supplies-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="supplies-container">
      {/* Header */}
      <div className="supplies-header">
        <div className="header-content">
          <h1>Gestión de Insumos</h1>
          <p>Administra el inventario de suministros dentales</p>
        </div>
        <button
          className="btn-primary add-btn"
          onClick={() => setShowModal(true)}
        >
          Agregar Insumo
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="supplies-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={searchFilter}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchFilter && (
            <button
              className="clear-search-btn"
              onClick={clearSearch}
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={expiringOnly}
            onChange={handleExpiringToggleChange}
          />
          <span className="slider" aria-hidden="true"></span>
          <span className="toggle-label">Próximos a caducar (≤30 días)</span>
        </label>
      </div>

      {/* Supplies Table */}
      <div className="supplies-table-container">
        {filteredSupplies.length === 0 ? (
          <div className="no-supplies">
            <p>No se encontraron insumos</p>
            {searchFilter && (
              <button className="btn-secondary" onClick={clearSearch}>
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <table className="supplies-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Contador</th>
                <th>Caducidad</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredSupplies.map((supply) => {
                const du = daysUntil(supply?.expirationDate);
                return (
                  <tr key={supply.id}>
                    <td className="supply-name">
                      <div className="supply-details">
                        <span className="name">{supply.name}</span>
                        
                      </div>
                    </td>
                    <td className="supply-counter">
                      {supply.counter !== undefined && supply.counter !== null ? supply.counter : '-'}
                    </td>
                    <td className="supply-expiration">
                      <span className="date-value">
                        {formatDate(supply?.expirationDate)}
                      </span>
                      {du === null ? null : du < 0 ? (
                        <span className="badge badge-danger">Vencido</span>
                      ) : du <= 30 ? (
                        <span className="badge badge-warning">Caduca en {du} días</span>
                      ) : null}
                    </td>
                    
                    <td className="supply-actions">
                      <button
                        className="btn-secondary edit-btn"
                        onClick={() => handleEdit(supply)}
                        title="Editar insumo"
                      >
                        Editar
                      </button>
                      <button
                        className="btn-danger delete-btn"
                        onClick={() => handleDelete(supply)}
                        title="Eliminar insumo"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingSupply ? 'Editar Insumo' : 'Agregar Nuevo Insumo'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">
                    Nombre <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Hilo dental, Anestesia, etc."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="expirationDate">Fecha de caducidad</label>
                  <input
                    type="date"
                    id="expirationDate"
                    name="expirationDate"
                    value={formData.expirationDate}
                    onChange={handleInputChange}
                    ref={expirationInputRef}
                    onClick={openNativeDatePicker}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="counter">Contador (Opcional)</label>
                  <input
                    type="number"
                    id="counter"
                    name="counter"
                    value={formData.counter}
                    onChange={handleInputChange}
                    placeholder="Cantidad"
                    min="0"
                  />
                </div>
              </div>

              

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  icon="fas fa-save"
                >
                  {isLoading
                    ? (editingSupply ? 'Actualizando...' : 'Creando...')
                    : (editingSupply ? 'Actualizar' : 'Crear')
                  }
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Confirmar Eliminación</h3>
              <button className="modal-close" onClick={handleCloseDeleteModal}>
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <p>
                ¿Estás seguro de que deseas eliminar el insumo{' '}
                <strong>"{deletingSupply?.name}"</strong>?
              </p>
              <p className="warning-text">
                Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseDeleteModal}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Supplies;